import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  Link,
  Menu,
  MenuItem,
  Popover,
  Toolbar,
  Typography,
} from '@mui/material';
import { Menu as MenuIcon, MoreVert as MoreVertIcon } from '@mui/icons-material';
import { useState } from 'react';

const drawerWidth = 272;

function NavigationItem({ item, onSelectItem }) {
  return (
    <ListItemButton
      onClick={() => onSelectItem?.(item.id)}
      selected={Boolean(item.active)}
      sx={{
        mb: 0.5,
        minHeight: 44,
        px: 1.25,
      }}
    >
      {item.icon ? <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>{item.icon}</ListItemIcon> : null}
      <Typography sx={{ fontSize: 14, fontWeight: item.active ? 700 : 500 }} variant="body2">
        {item.label}
      </Typography>
    </ListItemButton>
  );
}

function NavigationSection({ section, onSelectItem }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ color: 'text.secondary', letterSpacing: '0.16em', px: 1 }} variant="overline">
        {section.title}
      </Typography>
      <List dense disablePadding sx={{ mt: 1 }}>
        {section.items.map((item) => (
          <NavigationItem item={item} key={item.id ?? item.label} onSelectItem={onSelectItem} />
        ))}
      </List>
    </Box>
  );
}

function BottomAccountCard({ account, onSelectItem, onSignOut }) {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [helpAnchorEl, setHelpAnchorEl] = useState(null);

  if (!account) {
    return null;
  }

  const menuOpen = Boolean(menuAnchorEl);
  const helpOpen = Boolean(helpAnchorEl);

  function handleOpenMenu(event) {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  }

  function handleCloseMenu() {
    setMenuAnchorEl(null);
  }

  function handleOpenHelp(event) {
    event.preventDefault();
    event.stopPropagation();
    setHelpAnchorEl(event.currentTarget);
  }

  function handleCloseHelp() {
    setHelpAnchorEl(null);
  }

  function handleSelectAccount() {
    handleCloseMenu();
    onSelectItem?.(account.id);
  }

  function handleSignOut() {
    handleCloseMenu();
    onSignOut?.();
  }

  return (
    <>
      <ListItemButton
        onClick={() => onSelectItem?.(account.id)}
        selected={Boolean(account.active)}
        sx={{
          alignItems: 'flex-start',
          minHeight: 72,
          px: 1.5,
          py: 1.25,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.2 }} variant="body1">
            {account.title}
          </Typography>
          <Typography color="text.secondary" noWrap sx={{ mt: 0.35 }} variant="body2">
            {account.subtitle}
          </Typography>
          <Link
            aria-controls={helpOpen ? 'bottom-account-help-popover' : undefined}
            aria-expanded={helpOpen ? 'true' : undefined}
            component="button"
            onClick={handleOpenHelp}
            sx={{
              color: 'primary.main',
              display: 'inline-flex',
              fontSize: 12,
              fontWeight: 700,
              lineHeight: 1.4,
              mt: 0.5,
              p: 0,
              textAlign: 'left',
              textDecorationColor: 'currentColor',
            }}
            type="button"
            variant="body2"
          >
            Need help?
          </Link>
        </Box>
        <IconButton
          aria-controls={menuOpen ? 'bottom-account-menu' : undefined}
          aria-expanded={menuOpen ? 'true' : undefined}
          aria-haspopup="menu"
          aria-label="Open account menu"
          onClick={handleOpenMenu}
          size="small"
          sx={{
            alignSelf: 'center',
            color: 'text.secondary',
            ml: 1,
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </ListItemButton>
      <Menu
        anchorEl={menuAnchorEl}
        id="bottom-account-menu"
        onClose={handleCloseMenu}
        open={menuOpen}
        transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <MenuItem onClick={handleSelectAccount}>Account</MenuItem>
        <MenuItem onClick={handleSignOut}>Logout</MenuItem>
      </Menu>
      <Popover
        anchorEl={helpAnchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        id="bottom-account-help-popover"
        onClose={handleCloseHelp}
        open={helpOpen}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        <Box sx={{ maxWidth: 232, p: 1.5 }}>
          <Typography sx={{ fontSize: 13, lineHeight: 1.55 }} variant="body2">
            Email{' '}
            <Link href="mailto:support@ta2shera.com" onClick={(event) => event.stopPropagation()}>
              support@ta2shera.com
            </Link>{' '}
            and our team will get back to you as soon as possible!
          </Typography>
        </Box>
      </Popover>
    </>
  );
}

function NavigationContent({ bottomAccount, onSelectItem, onSignOut, sections }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 1, pb: 2 }}>
        <Typography sx={{ letterSpacing: '0.22em' }} variant="overline">
          Ta2shera.com
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {sections.map((section) => (
          <NavigationSection key={section.title} onSelectItem={onSelectItem} section={section} />
        ))}
      </Box>

      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          mt: 'auto',
          pt: 2,
        }}
      >
        <BottomAccountCard account={bottomAccount} onSelectItem={onSelectItem} onSignOut={onSignOut} />
      </Box>
    </Box>
  );
}

function AppShell({ children, bottomAccount, onSelectItem, onSignOut, sections, title }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  function handleSelectItem(itemId) {
    onSelectItem?.(itemId);
    setMobileOpen(false);
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        color="inherit"
        elevation={0}
        position="fixed"
        sx={{
          display: { md: 'none' },
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Toolbar sx={{ minHeight: 64 }}>
          <IconButton aria-label="Open portal navigation" edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 1 }}>
            <MenuIcon />
          </IconButton>
          {title ? (
            <Typography sx={{ fontWeight: 700 }} variant="h6">
              {title}
            </Typography>
          ) : null}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        onClose={() => setMobileOpen(false)}
        open={mobileOpen}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            px: 2,
            py: 2.5,
          },
        }}
        variant="temporary"
      >
        <NavigationContent
          bottomAccount={bottomAccount}
          onSelectItem={handleSelectItem}
          onSignOut={onSignOut}
          sections={sections}
        />
      </Drawer>

      <Drawer
        anchor="left"
        open
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            px: 2,
            py: 2.5,
          },
        }}
      >
        <NavigationContent
          bottomAccount={bottomAccount}
          onSelectItem={handleSelectItem}
          onSignOut={onSignOut}
          sections={sections}
        />
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0 }}>
        <Toolbar sx={{ display: { xs: 'flex', md: 'none' } }} />
        <Box
          sx={{
            px: { xs: 2, sm: 3, lg: 4 },
            py: { xs: 2, sm: 3, lg: 4 },
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

export default AppShell;
